import type { HttpStatus } from '@montugroup/apis-styleguide';
import { apiV1Contract } from '@montugroup/circuit-api-contracts';
import { isMontuError } from '@montugroup/circuit-shared/utils/typscript-helpers';
import { Box, Link, Typography } from '@montugroup/design-system';
import { styled } from '@mui/material';
import type { ClientInferResponseBody } from '@ts-rest/core';
import { isErrorResponse } from '@ts-rest/core';
import type { ReactElement } from 'react';
import { MdOutlineWarningAmber } from 'react-icons/md';
import { Link as RouterLink } from 'react-router-dom';

const MessageBox = styled(Box)(({ theme }) => ({
  padding: `${theme.spacing(1.5)} ${theme.spacing(4)}`,
  border: `1px solid ${theme.palette.warning.main}`,
  borderRadius: theme.spacing(1),
  display: 'flex',
  gap: theme.spacing(4),
  marginBottom: theme.spacing(10),
  textAlign: 'left',
}));

const IconContainer = styled(Box)(({ theme }) => ({
  height: theme.spacing(5.5),
  width: theme.spacing(5.5),
  padding: `${theme.spacing(1.75)} ${theme.spacing(3)} ${theme.spacing(1.75)} 0`,
  color: theme.palette.warning.main,
}));

const TextContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  padding: `${theme.spacing(2)} 0`,
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: theme.spacing(1),
  flex: '1 0 0',
}));

const customErrorCodes: Record<string, ReactElement> = {
  'password.reset_required': (
    <Typography>
      <b>Your password has expired</b> and needs to be updated to continue. Please{' '}
      <Link component={RouterLink} to={'/forgot-password'}>
        reset your password
      </Link>{' '}
      to regain access to your account.{' '}
      <span data-testid="support-text">Contact support if you&apos;re still having issues.</span>
    </Typography>
  ),
};

const CustomError = ({ error }: { error: { code: keyof typeof customErrorCodes } }) => {
  return (
    <Typography>
      <b>{customErrorCodes[error.code]}</b>
    </Typography>
  );
};

const StandardError = ({ error, showSupportText }: { error: string; showSupportText?: boolean }) => {
  return (
    <Typography>
      <b>{error}</b>
      {showSupportText && <span data-testid="support-text">Contact support if you&apos;re still having issues.</span>}
    </Typography>
  );
};

function LoginFailure({
  error,
  showSupportText,
}: {
  error: string | ClientInferResponseBody<typeof apiV1Contract.user.login, typeof HttpStatus.FORBIDDEN>;
  showSupportText?: boolean;
}) {
  if (!error) {
    return null;
  }

  const isCustomError =
    isErrorResponse(error, apiV1Contract.user.login) && isMontuError<typeof apiV1Contract.user.login>(error);

  return (
    <MessageBox role="alert">
      <IconContainer>
        <MdOutlineWarningAmber size={22} />
      </IconContainer>
      <TextContainer>
        {isCustomError ? (
          <CustomError error={error.body.error} />
        ) : (
          typeof error === 'string' && <StandardError error={error} showSupportText={showSupportText} />
        )}
      </TextContainer>
    </MessageBox>
  );
}

export default LoginFailure;
